import {
  addToCollection,
  updateCollectionItem,
  removeFromCollection,
  removeFromCollectionByCardId,
  updateCollectionByCard,
  type CollectionItemUpdate,
} from '@/services/api/collection'
import type { CollectionItemCondition } from '@/types'
import {
  COLLECTION_MUTATION_KEYS,
  COLLECTION_SCOPE,
  COLLECTION_WRITE_SETTLED_KEYS,
  COLLECTION_REMOVE_SETTLED_KEYS,
} from './collectionKeys'

/**
 * @public Never imported by name: consumed at runtime by `lib/offlineMutations.ts`
 * (import.meta.glob over hooks/**) to register offline-replay defaults in
 * main.tsx. Un-exporting this silently breaks replay.
 */
export const collectionMutations = {
  add: {
    mutationKey: COLLECTION_MUTATION_KEYS.add,
    mutationFn: ({
      cardId,
      data,
      idempotencyKey,
    }: {
      cardId: number
      data?: { quantity?: number; notes?: string; acquiredPrice?: number; condition?: CollectionItemCondition | null }
      idempotencyKey?: string
    }) =>
      addToCollection(
        cardId,
        data?.quantity,
        data?.acquiredPrice,
        data?.notes,
        idempotencyKey,
        data?.condition ?? undefined,
      ),
    offlineReplay: true,
    scope: COLLECTION_SCOPE,
    settledInvalidation: COLLECTION_WRITE_SETTLED_KEYS,
  },
  update: {
    mutationKey: COLLECTION_MUTATION_KEYS.update,
    mutationFn: ({ id, data, idempotencyKey }: { id: number; data: CollectionItemUpdate; idempotencyKey?: string }) =>
      updateCollectionItem(id, data, idempotencyKey),
    offlineReplay: true,
    scope: COLLECTION_SCOPE,
    settledInvalidation: COLLECTION_WRITE_SETTLED_KEYS,
  },
  updateByCard: {
    mutationKey: COLLECTION_MUTATION_KEYS.updateByCard,
    mutationFn: ({
      cardId,
      data,
      idempotencyKey,
    }: {
      cardId: number
      data: CollectionItemUpdate
      idempotencyKey?: string
    }) => updateCollectionByCard(cardId, data, idempotencyKey),
    offlineReplay: true,
    scope: COLLECTION_SCOPE,
    settledInvalidation: COLLECTION_WRITE_SETTLED_KEYS,
  },
  remove: {
    mutationKey: COLLECTION_MUTATION_KEYS.remove,
    mutationFn: ({ id, idempotencyKey }: { id: number; idempotencyKey?: string }) =>
      removeFromCollection(id, idempotencyKey),
    offlineReplay: true,
    scope: COLLECTION_SCOPE,
    settledInvalidation: COLLECTION_REMOVE_SETTLED_KEYS,
  },
  removeByCard: {
    mutationKey: COLLECTION_MUTATION_KEYS.removeByCard,
    mutationFn: ({ cardId, idempotencyKey }: { cardId: number; idempotencyKey?: string }) =>
      removeFromCollectionByCardId(cardId, idempotencyKey),
    offlineReplay: true,
    scope: COLLECTION_SCOPE,
    settledInvalidation: COLLECTION_REMOVE_SETTLED_KEYS,
  },
}
